// Copyright (c) 2026 Michael F. Kamprath
//
// tap.js -- Touch-reaction animation engine for Ewe Can Count.
//
// When a child taps a countable object, this module:
//   1. Pauses the tapped sprite's idle animation
//   2. Picks a random reaction from the theme's touchReactions pool
//   3. Plays the reaction's keyframe cycle (texture swaps, rotation,
//      scale) using the same sampleCycle interpolation as entrance.js
//   4. Fires any per-keyframe sound effect
//   5. Restores the static texture, rotation, and scale
//   6. Resumes the sprite's idle animation
//
// Double-tap guard: while a reaction is playing on a sprite, further
// taps on that sprite are ignored (sprite._reacting flag set in
// scene.js).
//
// The tap manager is created after the entrance animation completes
// and destroyed on scene teardown.

import * as PIXI from 'pixi.js';
import {
  animateOver,
  sampleCycle,
  cycleNaturalDuration,
  setSpriteTexture,
  setSpriteRotation,
} from './entrance.js';
import { play as playOneShot } from '../audio/engine.js';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/** Get the inner Sprite from a container (duck-typed _inner field). */
function getInner(obj) {
  return obj._inner || obj;
}

/**
 * Resolve a keyframe image path to a PIXI Texture, using the pre-loaded
 * texture map when available, falling back to Assets.get.
 */
function resolveTexture(imagePath, textureMap) {
  if (!imagePath) return null;
  if (textureMap) {
    const tex = (typeof textureMap.get === 'function')
      ? textureMap.get(imagePath)
      : textureMap[imagePath];
    if (tex) return tex;
  }
  const url = imagePath.startsWith('/') ? imagePath : `/${imagePath}`;
  return PIXI.Assets.get(url) || null;
}

// ---------------------------------------------------------------------------
// Texture + sound preloading
// ---------------------------------------------------------------------------

/**
 * Collect all image paths and sound URLs referenced by a theme's
 * touchReactions so they can be preloaded before the tap system starts.
 *
 * @param {object} touchReactions  The theme's touchReactions block from
 *                                 animations.json
 * @returns {{ images: string[], sounds: string[] }}
 */
export function collectTouchReactionAssets(touchReactions) {
  const images = new Set();
  const sounds = new Set();
  if (!touchReactions?.cycles) return { images: [], sounds: [] };

  for (const name of Object.keys(touchReactions.cycles)) {
    const keyframes = touchReactions.cycles[name];
    if (!Array.isArray(keyframes)) continue;
    for (const k of keyframes) {
      if (k.image) images.add(k.image);
      if (k.sound) sounds.add(k.sound);
    }
  }
  return { images: [...images], sounds: [...sounds] };
}

/**
 * Preload all textures needed for touch-reaction animations.
 * Returns a plain object map { path: Texture }.
 *
 * @param {object} touchReactions
 * @returns {Promise<Object>}
 */
export async function preloadTouchReactionTextures(touchReactions) {
  const { images } = collectTouchReactionAssets(touchReactions);
  if (import.meta.env.DEV) {
    // eslint-disable-next-line no-console
    console.log('[tap] preloading textures:', images);
  }
  const map = {};
  const { Assets } = PIXI;
  if (!Assets || images.length === 0) return map;

  // Load each texture individually so a single broken/contested image
  // cannot take down the entire batch (PixiJS v8's batch Assets.load
  // rejects ALL textures if ANY one fails — e.g. a createImageBitmap
  // race on a re-registered alias).
  for (const p of images) {
    const alias = '/' + p;

    // If the texture is already in the cache (e.g. cow_static_image
    // loaded by scene.js), grab it directly — no need to re-load.
    const cached = Assets.get(alias);
    if (cached) {
      map[p] = cached;
      continue;
    }

    try {
      Assets.add({ alias, src: alias });
    } catch {
      /* "already has key" warning is non-fatal */
    }
    try {
      const tex = await Assets.load(alias);
      if (tex) map[p] = tex;
    } catch (err) {
      if (import.meta.env.DEV) {
        // eslint-disable-next-line no-console
        console.warn(`[tap] failed to load ${alias}:`, err);
      }
    }
  }
  return map;
}

// ---------------------------------------------------------------------------
// Single-sprite reaction playback
// ---------------------------------------------------------------------------

/**
 * Play a single touch-reaction cycle on a sprite.
 *
 * The cycle drives texture swaps, rotation, and scale through
 * sampleCycle (the same interpolation entrance.js uses).  The sprite's
 * base position stays fixed (x1==x2, y1==y2).
 *
 * Touch-reaction keyframes use `sound` (not `soundFile`) for their
 * audio trigger, matching the animations.json convention established
 * for touchReactions.
 *
 * @param {object}  sprite      The object container (scene.js duck-type)
 * @param {Array}   keyframes   Cycle keyframe array
 * @param {object}  textureMap  Pre-loaded { path: Texture }
 * @param {{ cancelled: boolean }} cancel  Cancellation token
 * @returns {Promise<void>}
 */
async function playReactionCycle(sprite, keyframes, textureMap, cancel) {
  if (!keyframes || keyframes.length === 0) return;

  const inner = getInner(sprite);
  const flipped = inner.scale.x < 0;
  const cycleTime = cycleNaturalDuration(keyframes);
  if (cycleTime <= 0) return;

  const durationMs = cycleTime * 1000;
  let lastImage = null;
  const firedSounds = new Set();

  await animateOver(durationMs, (t) => {
    // Map normalized time to cycle time.  At t=1, clamp to cycleTime
    // so the final keyframe renders (not wrapped to 0).
    const cycleT = t >= 1 ? cycleTime : t * cycleTime;
    const sample = sampleCycle(keyframes, cycleT, cycleTime);

    // Texture swap
    if (sample.image && sample.image !== lastImage) {
      const tex = resolveTexture(sample.image, textureMap);
      if (import.meta.env.DEV) {
        // eslint-disable-next-line no-console
        console.log(`[tap] t=${cycleT.toFixed(3)} swap → ${sample.image} tex=${tex ? 'OK' : 'MISSING'}`);
      }
      if (tex) {
        setSpriteTexture(sprite, tex);
        lastImage = sample.image;
      }
    }

    // Rotation -- mirror when flipped so the visual spin direction
    // is consistent with the sprite's facing.
    const rotDeg = flipped ? -sample.rotationDeg : sample.rotationDeg;
    setSpriteRotation(sprite, rotDeg * Math.PI / 180);

    // Scale -- multiply against the base scale set in scene.js so the
    // sprite's resting size stays correct.  Preserve flip sign on X.
    const baseScaleX = sprite._baseScaleX ?? 1;
    const baseScaleY = sprite._baseScaleY ?? 1;
    const flipSign = flipped ? -1 : 1;
    const xs = flipSign * baseScaleX * sample.scaleX;
    const ys = baseScaleY * sample.scaleY;
    if (inner.scale.x !== xs) inner.scale.x = xs;
    if (inner.scale.y !== ys) inner.scale.y = ys;

    // Sound triggers.  Touch-reaction keyframes use `sound` (not
    // `soundFile`).  Fire each sound once per cycle.
    for (const k of keyframes) {
      if (!k.sound) continue;
      const soundKey = k.t.toString();
      if (cycleT >= k.t && !firedSounds.has(soundKey)) {
        firedSounds.add(soundKey);
        playOneShot('/' + k.sound);
      }
    }
  }, cancel);

  // Restore resting state: static texture, zero rotation, base scale.
  if (sprite._staticTexture) setSpriteTexture(sprite, sprite._staticTexture);
  setSpriteRotation(sprite, 0);
  const baseScaleX = sprite._baseScaleX ?? 1;
  const baseScaleY = sprite._baseScaleY ?? 1;
  const flipSign = flipped ? -1 : 1;
  inner.scale.x = flipSign * baseScaleX;
  inner.scale.y = baseScaleY;
}

// ---------------------------------------------------------------------------
// Tap manager (scene-level)
// ---------------------------------------------------------------------------

/**
 * Create a tap manager for a scene's countable objects.
 *
 * @param {object}   opts
 * @param {Array}    opts.sprites         Array of object containers
 * @param {object}   opts.touchReactions  The theme's touchReactions block
 * @param {object}   opts.textureMap      Pre-loaded { path: Texture }
 * @param {object}   opts.idleManager     The scene's idle manager (for
 *                                        pauseSprite / resumeSprite)
 * @param {() => void} [opts.onTap]       Invoked on every countable-object
 *                                        tap (used by the Quiz round to
 *                                        count `object_taps` for telemetry).
 *                                        Fires even on double-tap-guarded
 *                                        mid-reaction re-taps -- the guard
 *                                        only suppresses the ANIMATION, the
 *                                        telemetry counter still increments
 *                                        so we see real interaction volume.
 * @returns {{ destroy(): void }}
 */
export function createTapManager({ sprites, touchReactions, textureMap, idleManager, onTap }) {
  if (!touchReactions?.reactions?.length || !touchReactions?.cycles) {
    // Theme has no touch reactions -- return a no-op manager.
    return { destroy() {} };
  }

  const reactionNames = touchReactions.reactions;
  const cycles = touchReactions.cycles;
  let destroyed = false;

  // Per-sprite pointerdown handler.  Stored so we can remove it on destroy.
  const handlers = [];

  for (let i = 0; i < sprites.length; i++) {
    const sprite = sprites[i];
    const index = i;

    const handler = () => {
      if (destroyed) return;
      // Telemetry: every tap on a countable object counts toward
      // `object_taps`, even ones the double-tap guard below swallows.
      try { onTap?.(); } catch { /* non-fatal */ }
      if (sprite._reacting) return; // double-tap guard

      sprite._reacting = true;

      // Pick a random reaction from the pool.
      const name = reactionNames[Math.floor(Math.random() * reactionNames.length)];
      const keyframes = cycles[name];
      if (!keyframes || keyframes.length === 0) {
        sprite._reacting = false;
        return;
      }

      if (import.meta.env.DEV) {
        const texKeys = Object.keys(textureMap);
        // eslint-disable-next-line no-console
        console.log(`[tap] sprite ${index} → reaction "${name}", ${keyframes.length} keyframes, textureMap has ${texKeys.length} entries`);
      }

      // Pause this sprite's idle animation while the reaction plays.
      if (idleManager) idleManager.pauseSprite(index);

      const cancel = { cancelled: false };

      playReactionCycle(sprite, keyframes, textureMap, cancel)
        .catch(() => { /* swallow errors so idle always resumes */ })
        .finally(() => {
          sprite._reacting = false;
          if (!destroyed && idleManager) {
            idleManager.resumeSprite(index);
          }
        });
    };

    sprite.on('pointerdown', handler);
    handlers.push({ sprite, handler });
    if (import.meta.env.DEV) {
      // eslint-disable-next-line no-console
      console.log(`[tap] registered pointerdown on sprite ${index} at (${sprite.x.toFixed(0)}, ${sprite.y.toFixed(0)})`);
    }
  }

  return {
    destroy() {
      destroyed = true;
      for (const { sprite, handler } of handlers) {
        sprite.off('pointerdown', handler);
        // If any reaction is in mid-flight, the _reacting flag stays
        // true until the promise resolves.  The `destroyed` guard
        // prevents resumeSprite from running on a torn-down idle
        // manager.  The sprite's visual state will be cleaned up by
        // the scene's destroy() call.
      }
      handlers.length = 0;
    },
  };
}
